import { Button, Form, Input } from "antd";
import { Element, scroller } from "react-scroll";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import TaskPanel from "Components/TaskPanel";
import { StatusType } from "App.types";
import styles from "./ChatWithAi.module.scss";
import API from "Api";
import { useSuspenseQuery } from "@tanstack/react-query";
import ChatWithAiMessage from "./ChatWithAiMessage";
import { SendOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import { flatten } from "lodash";
import { useNavigate } from "react-router-dom";

type ChatMessageType = { role: "user" | "model"; text: string; translate?: string; isCompleted?: boolean };

const ChatWithAi: FC<{ onComplete?: () => any; storyId: number }> = ({ onComplete, storyId }) => {
  const [status, setStatus] = useState<StatusType>();
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [loading, setLoading] = useState(false);

  const { data: history } = useSuspenseQuery({
    queryKey: ["messages", storyId],
    queryFn: () => API.chat.answers(storyId),
  });

  useEffect(() => {
    const [, ...lastMessages] = flatten(
      history.map((m) =>
        m.role === "model"
          ? m.text
              .split("\n")
              .filter((t) => t.trim())
              .map((t) => ({ text: t.trim(), role: "model" }))
          : [m],
      ),
    );
    //@ts-ignore
    setMessages(lastMessages);

    if (history.some((m) => m.isCompleted)) {
      setStatus(StatusType.Completed);
    }
  }, [history]);

  const newMessagesRef = useRef<ChatMessageType[]>([]);

  const [form] = useForm();

  const navigate = useNavigate();

  const sendMessage = useCallback(
    async ({ text }: any) => {
      if (!status || text.trim()) {
        if (text.trim()) {
          form.resetFields();

          if (status === StatusType.Error) {
            setMessages((prev) => {
              prev.pop();
              return [...prev, { role: "user", text }];
            });
          } else {
            setMessages((prev) => [...prev, { role: "user", text }]);
          }
        }

        setLoading(true);
        const response = await API.chat.answer({ storyId, message: text.replace(/^\n/, "") || "." }).finally(() => setLoading(false));

        setStatus(StatusType.Empty);

        if (response.text) {
          const newMessages: ChatMessageType[] = response.text
            .split("\n")
            .filter((t) => t.trim())
            .map((t) => ({ text: t.trim(), role: "model" }));
          const [message, ...restMessages] = newMessages;
          newMessagesRef.current = restMessages;
          setMessages((prev) => [...prev, message]);
        }
        if (response.isCompleted) {
          setStatus(StatusType.Completed);
          //onComplete();
        }
      }
    },
    [status, storyId, form],
  );

  useEffect(() => {
    if (!history?.length) {
      sendMessage({ text: "" }).then(() => setStatus(StatusType.Empty));
    } else {
    }
  }, [history, sendMessage]);

  useEffect(() => {
    if (messages.length) {
      scroller.scrollTo(`${messages.length - 1}`, { smooth: true, isDynamic: true, offset: -100 });
    }
  }, [messages.length]);

  const onModelMessageTypeCompleted = useCallback(() => {
    scroller.scrollTo("bottom", {});
    if (newMessagesRef.current.length) {
      const [message, ...restMessages] = newMessagesRef.current;
      newMessagesRef.current = restMessages;
      setMessages((prev) => [...prev, message]);
    }
  }, []);

  return (
    <div className={styles.chatWithAi}>
      <div className={styles.messages}>
        {messages.map((message, idx) => (
          <Element name={`${idx}`} key={idx}>
            <ChatWithAiMessage
              //info={idx === messages.length - 1 && status === StatusType.Error ? comments : ""}
              active={idx === messages.length - 1 && !!status}
              message={message}
              onCompleted={onModelMessageTypeCompleted}
            />
          </Element>
        ))}
        {loading && (
          <div className={styles.typing}>
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <div className={styles.placeholder} />
      <Element name={"bottom"} />

      <TaskPanel
        isCompleted={status === StatusType.Completed}
        rightButton={<div />}
        task={undefined}
        lessonId={""}
        sentId={undefined}
        storyId={undefined}
        onNext={onComplete || (() => navigate("/courses"))}
        nextButton={"Выбрать курс"}
      >
        <Form onFinish={sendMessage} form={form} className={styles.input}>
          <Form.Item name={"text"} rules={[{ required: true }]} noStyle>
            <Input.TextArea onPressEnter={() => form.submit()} />
          </Form.Item>
        </Form>
        <Button disabled={loading} icon={<SendOutlined />} type={"link"} onClick={() => form.submit()} />
      </TaskPanel>
    </div>
  );
};

export default ChatWithAi;
